import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { FloppyDisk, Check } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export function SalesGoalsPanel({ year }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [goals, setGoals] = useState(MONTHS.map((_, i) => ({ month: i + 1, goal_amount: 0 })));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/sales-goals`, { params: { year }, headers: { Authorization: `Bearer ${token}` } });
        const merged = MONTHS.map((_, i) => {
          const existing = data.find(g => g.month === i + 1);
          return { month: i + 1, goal_amount: existing?.goal_amount || 0 };
        });
        setGoals(merged);
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    };
    load();
  }, [year, token]);

  const updateGoal = (monthIdx, val) => {
    setGoals(prev => prev.map((g, i) => i === monthIdx ? { ...g, goal_amount: parseFloat(val) || 0 } : g));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const g of goals) {
        await axios.post(`${API}/admin/sales-goals`, { year, month: g.month, goal_amount: g.goal_amount }, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success("Metas guardadas");
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  const totalGoal = goals.reduce((s, g) => s + g.goal_amount, 0);

  return (
    <div className="space-y-4" data-testid="goals-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-slate-700">Metas de Venta {year}</h2>
          <p className="text-sm text-slate-400">Define la meta de ingresos mensual para medir el avance del equipo</p>
        </div>
        {isAdmin && <button onClick={saveAll} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-50" data-testid="save-goals-btn">
          <FloppyDisk size={16} /> {saving ? "Guardando..." : "Guardar Todo"}
        </button>}
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Mes</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Meta de Ventas (MXN)</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {goals.map((g, i) => (
              <tr key={g.month} className="hover:bg-slate-50" data-testid={`goal-row-${g.month}`}>
                <td className="px-4 py-3 font-medium text-slate-700">{MONTHS[i]}</td>
                <td className="px-4 py-3">
                  {isAdmin ? (
                    <input type="number" value={g.goal_amount || ""} onChange={e => updateGoal(i, e.target.value)}
                      placeholder="0" className="px-3 py-1.5 border rounded-lg text-sm w-48" data-testid={`goal-input-${g.month}`} />
                  ) : (
                    <span className="text-sm font-medium text-slate-700">{fmt(g.goal_amount)}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {g.goal_amount > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><Check size={12} /> {fmt(g.goal_amount)}</span>
                  ) : (
                    <span className="text-xs text-slate-400">Sin meta</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 border-t">
            <tr>
              <td className="px-4 py-3 font-bold text-slate-700">Total Anual</td>
              <td className="px-4 py-3 font-bold text-blue-700">{fmt(totalGoal)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
